<template>
  <div>
    <div class="ext-section-number">Explorer</div>
    <div class="ext-section-title">Claims Explorer</div>
    <div class="ext-section-desc" style="margin-bottom:16px;">
      Drag fields into Rows, Values, and Filters — results update live.
    </div>

    <!-- No entity scope prompt -->
    <div v-if="!entityId" style="text-align:center; padding:60px 0; color:#A9A29A; font-size:12px;">
      Select a practice to explore claims.
    </div>

    <template v-else>
      <!-- PANEL SCOPE BAR -->
      <div class="ext-table-wrap" style="padding:12px 16px; margin-bottom:16px;">
        <div style="display:flex; flex-wrap:wrap; gap:12px; align-items:flex-end;">
          <div style="flex:0 0 auto;">
            <div class="filter-label">Dataset</div>
            <select v-model="metricId" class="filter-input" style="width:200px;" @change="onDatasetChange">
              <option v-for="ds in datasets" :key="ds.metricId" :value="ds.metricId">{{ ds.label }}</option>
            </select>
          </div>
          <div style="flex:0 0 auto;">
            <div class="filter-label">Attribution Panel</div>
            <select v-model="reportDt" class="filter-input" style="width:130px;" @change="onScopeChange">
              <option v-for="d in reportDates" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>
          <div style="flex:0 0 auto;">
            <div class="filter-label">From</div>
            <input v-model="clmFromDt" type="date" class="filter-input" style="width:130px;" @change="onScopeChange">
          </div>
          <div style="flex:0 0 auto;">
            <div class="filter-label">Through</div>
            <input v-model="clmThruDt" type="date" class="filter-input" style="width:130px;" @change="onScopeChange">
          </div>
          <div style="flex:0 0 auto;">
            <div class="filter-label">Care Setting</div>
            <select v-model="careSetting" class="filter-input" style="width:100px;" @change="onScopeChange">
              <option value="">All</option>
              <option value="SNF">SNF</option>
              <option value="ALF">ALF</option>
            </select>
          </div>
          <div style="flex:0 0 auto;">
            <div class="filter-label">Scope</div>
            <select v-model="entityType" class="filter-input" style="width:130px;" @change="onScopeChange">
              <option value="">Entity Total</option>
              <option value="Facility">By Facility</option>
              <option value="Staff">By Provider</option>
            </select>
          </div>
          <div style="flex:1 1 auto;" />
          <div v-if="metricId === 922" style="flex:0 0 auto;">
            <div class="filter-label">Quick Views</div>
            <div style="display:flex; gap:6px;">
              <button v-for="qv in quickViews" :key="qv.id" class="qv-btn" @click="applyQuickView(qv)">
                {{ qv.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- CATALOG LOADING -->
      <div v-if="catalogLoading" style="text-align:center; padding:40px 0; color:#A9A29A; font-size:12px;">
        Loading fields…
      </div>

      <!-- TWO-COLUMN: results (left) | field panel (right) -->
      <div v-else class="explorer-grid">
        <!-- LEFT: results -->
        <div class="results-pane">
          <div v-if="loading" style="text-align:center; padding:40px 0; color:#A9A29A; font-size:12px;">
            Running…
          </div>
          <div v-else-if="error" style="text-align:center; padding:40px 0; color:#B91C1C; font-size:12px;">
            {{ error }}
          </div>
          <div v-else-if="!spec.dimensions.length && !spec.values.length" style="text-align:center; padding:60px 0; color:#A9A29A; font-size:12px;">
            Drag a field into Rows or Values to see results.
          </div>
          <div v-else-if="!rows.length" style="text-align:center; padding:60px 0; color:#A9A29A; font-size:12px;">
            No results for this pivot.
          </div>
          <div v-else>
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
              <div style="font-size:11px; color:#A9A29A;">
                {{ displayRows.length }}<span v-if="displayRows.length !== rows.length"> of {{ rows.length }}</span> row{{ displayRows.length === 1 ? '' : 's' }}
                <span v-if="capped" style="color:#B45309;"> · capped at {{ limit }}</span>
              </div>
              <ExportMenu :disabled="!rows.length" :exports="[{ type: 'csv', action: exportCsv }]" />
            </div>
            <div class="ext-table-wrap">
              <table class="ext-table">
                <thead>
                  <tr>
                    <th
                      v-for="c in resultColumns"
                      :key="c.key"
                      :class="{ num: c.num, sortable: true }"
                      @click="toggleSort(c.key)"
                    >
                      <span class="th-label">{{ c.label }}</span>
                      <span class="sort-ind">{{ sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</span>
                    </th>
                  </tr>
                  <tr class="filter-row">
                    <th v-for="c in resultColumns" :key="'flt-' + c.key" :class="{ num: c.num }">
                      <!-- numeric: operator + value(s) -->
                      <template v-if="c.num">
                        <div class="col-filter-num">
                          <select v-model="colFilters[c.key].op" class="col-filter-op">
                            <option value="">—</option>
                            <option value="gte">≥</option>
                            <option value="lte">≤</option>
                            <option value="eq">=</option>
                            <option value="between">range</option>
                          </select>
                          <template v-if="colFilters[c.key].op === 'between'">
                            <input v-model="colFilters[c.key].min" type="number" class="col-filter-input" placeholder="min">
                            <input v-model="colFilters[c.key].max" type="number" class="col-filter-input" placeholder="max">
                          </template>
                          <input
                            v-else-if="colFilters[c.key].op"
                            v-model="colFilters[c.key].value"
                            type="number"
                            class="col-filter-input"
                            placeholder="value"
                          >
                        </div>
                      </template>
                      <!-- text: contains + discrete value picker -->
                      <template v-else>
                        <div class="col-filter-text">
                          <input
                            v-model="colFilters[c.key].text"
                            type="text"
                            class="col-filter-input"
                            :class="{ sensitive: c.sensitive }" :data-demo-search="c.sensitive ? '' : null"
                            placeholder="contains…"
                          >
                          <button
                            class="col-filter-pick"
                            :class="{ active: (colFilters[c.key].selected || []).length }"
                            :title="(colFilters[c.key].selected || []).length ? (colFilters[c.key].selected.length + ' selected') : 'Pick values'"
                            @click.stop="openPicker(c)"
                          >▾</button>
                        </div>
                        <!-- picker dropdown -->
                        <div v-if="pickerKey === c.key" class="value-picker" @click.stop>
                          <div class="vp-head">
                            <input
                              v-model="pickerSearch"
                              type="text"
                              class="col-filter-input"
                              :class="{ sensitive: pickerIsIdentity }" :data-demo-search="pickerIsIdentity ? '' : null"
                              placeholder="search values…"
                            >
                          </div>
                          <div v-if="pickerLoading" class="vp-msg">Loading…</div>
                          <div v-else-if="pickerError" class="vp-msg vp-err">{{ pickerError }}</div>
                          <template v-else>
                            <div class="vp-actions">
                              <button class="vp-link" @click="pickAll()">All</button>
                              <button class="vp-link" @click="pickNone()">None</button>
                            </div>
                            <div class="vp-list">
                              <label v-for="opt in filteredPickerOptions" :key="opt" class="vp-item">
                                <input
                                  type="checkbox"
                                  :checked="(pickerCurrent() || []).includes(opt)"
                                  @change="togglePick(opt)"
                                >
                                <span class="vp-val" :class="{ sensitive: pickerIsIdentity }">{{ opt }}</span>
                              </label>
                              <div v-if="!filteredPickerOptions.length" class="vp-msg">No matches.</div>
                            </div>
                            <div class="vp-foot">
                              <button class="vp-apply" @click="pickerKey = null">Done</button>
                            </div>
                          </template>
                        </div>
                      </template>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in displayRows" :key="i">
                    <td v-for="c in resultColumns" :key="c.key" :class="{ num: c.num, sensitive: c.sensitive }">
                      {{ c.fmt(row[c.key]) }}
                    </td>
                  </tr>
                  <tr v-if="displayRows.length > 1" class="total-row">
                    <td v-for="(c, ci) in resultColumns" :key="'t-' + c.key" :class="{ num: c.num, sensitive: ci > 0 && c.sensitive }">
                      <template v-if="ci === 0">Σ shown rows</template>
                      <template v-else-if="c.num && c.summable">{{ c.fmt(columnTotal(c.key)) }}</template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- RIGHT: stacked field panel -->
        <div class="field-pane">
          <!-- Run -->
          <button class="run-btn" :disabled="loading || (!spec.dimensions.length && !spec.values.length)" @click="runNow">
            {{ loading ? 'Running…' : 'Run' }}
          </button>

          <!-- Available Fields (flat, draggable) -->
          <div class="drop-box available-box">
            <div class="drop-title">Fields</div>
            <div class="chip-bin">
              <span
                v-for="f in catalog"
                :key="f.name"
                class="chip"
                draggable="true"
                @dragstart="onDragStart(f.name, 'available', $event)"
              >{{ pretty(f.name) }}</span>
            </div>
          </div>

          <!-- Rows -->
          <div
            class="drop-box"
            :class="{ 'drop-over': dragOver === 'rows' }"
            @dragover.prevent="dragOver = 'rows'"
            @dragleave="dragOver = null"
            @drop="onDrop('rows', $event)"
          >
            <div class="drop-title">Rows</div>
            <div v-if="!spec.dimensions.length" class="drop-empty">Drag fields here</div>
            <div
              v-for="(d, i) in spec.dimensions"
              :key="'d-' + d"
              class="placed-chip"
              draggable="true"
              @dragstart="onDragStart(d, 'rows', $event, i)"
            >
              <span class="placed-label">{{ pretty(d) }}</span>
              <span class="placed-x" @click="spec.dimensions.splice(i, 1)">×</span>
            </div>
          </div>

          <!-- Values -->
          <div
            class="drop-box"
            :class="{ 'drop-over': dragOver === 'values' }"
            @dragover.prevent="dragOver = 'values'"
            @dragleave="dragOver = null"
            @drop="onDrop('values', $event)"
          >
            <div class="drop-title">Values</div>
            <div v-if="!spec.values.length" class="drop-empty">Drag fields here</div>
            <div
              v-for="(v, i) in spec.values"
              :key="'v-' + i"
              class="placed-chip"
              draggable="true"
              @dragstart="onDragStart(v.field, 'values', $event, i)"
            >
              <select v-model="v.agg" class="mini-select">
                <option v-for="a in aggOptions" :key="a" :value="a">{{ aggLabel(a) }}</option>
              </select>
              <span class="placed-label" style="flex:1 1 auto;">{{ pretty(v.field) }}</span>
              <span class="placed-x" @click="spec.values.splice(i, 1)">×</span>
            </div>
          </div>

          <!-- Filters -->
          <div
            class="drop-box"
            :class="{ 'drop-over': dragOver === 'filters' }"
            @dragover.prevent="dragOver = 'filters'"
            @dragleave="dragOver = null"
            @drop="onDrop('filters', $event)"
          >
            <div class="drop-title">Filters</div>
            <div v-if="!spec.filters.length" class="drop-empty">Drag fields here</div>
            <div v-for="(f, i) in spec.filters" :key="'f-' + i" class="placed-chip placed-chip-tall">
              <div style="display:flex; align-items:center; gap:4px; width:100%;">
                <span class="placed-label" style="flex:1 1 auto;">{{ pretty(f.field) }}</span>
                <select v-model="f.op" class="mini-select" @change="onFilterOpChange(f)">
                  <option v-for="op in opsFor(f.field)" :key="op" :value="op">{{ opLabel(op) }}</option>
                </select>
                <span class="placed-x" @click="spec.filters.splice(i, 1)">×</span>
              </div>
              <div v-if="!['is_null', 'not_null'].includes(f.op)" style="margin-top:4px; width:100%;">
                <template v-if="f.op === 'between'">
                  <input v-model="f.value[0]" class="mini-input" :class="{ sensitive: isIdentityField(f.field) }" :data-demo-search="isIdentityField(f.field) ? '' : null" :type="inputType(f.field)">
                  <span style="margin:0 3px; color:#A9A29A;">–</span>
                  <input v-model="f.value[1]" class="mini-input" :class="{ sensitive: isIdentityField(f.field) }" :data-demo-search="isIdentityField(f.field) ? '' : null" :type="inputType(f.field)">
                </template>
                <div v-else-if="f.op === 'in'" class="filter-in-row">
                  <input
                    v-model="f.valueText" class="mini-input" style="flex:1 1 auto; width:auto;"
                    :class="{ sensitive: isIdentityField(f.field) }" :data-demo-search="isIdentityField(f.field) ? '' : null"
                    placeholder="comma-separated" @change="onInChange(f)"
                  >
                  <button
                    class="col-filter-pick"
                    :class="{ active: Array.isArray(f.value) && f.value.length }"
                    :title="Array.isArray(f.value) && f.value.length ? (f.value.length + ' selected') : 'Pick values'"
                    @click.stop="openFilterPicker(f, i)"
                  >▾</button>
                  <div v-if="pickerKey === 'filter-' + i" class="value-picker" @click.stop>
                    <div class="vp-head">
                      <input v-model="pickerSearch" type="text" class="col-filter-input" :class="{ sensitive: pickerIsIdentity }" :data-demo-search="pickerIsIdentity ? '' : null" placeholder="search values…">
                    </div>
                    <div v-if="pickerLoading" class="vp-msg">Loading…</div>
                    <div v-else-if="pickerError" class="vp-msg vp-err">{{ pickerError }}</div>
                    <template v-else>
                      <div class="vp-actions">
                        <button class="vp-link" @click="pickAll()">All</button>
                        <button class="vp-link" @click="pickNone()">None</button>
                      </div>
                      <div class="vp-list">
                        <label v-for="opt in filteredPickerOptions" :key="opt" class="vp-item">
                          <input
                            type="checkbox"
                            :checked="(pickerCurrent() || []).includes(opt)"
                            @change="togglePick(opt)"
                          >
                          <span class="vp-val" :class="{ sensitive: pickerIsIdentity }">{{ opt }}</span>
                        </label>
                        <div v-if="!filteredPickerOptions.length" class="vp-msg">No matches.</div>
                      </div>
                      <div class="vp-foot">
                        <button class="vp-apply" @click="pickerKey = null">Done</button>
                      </div>
                    </template>
                  </div>
                </div>
                <input v-else v-model="f.value" class="mini-input" style="width:100%;" :class="{ sensitive: isIdentityField(f.field) }" :data-demo-search="isIdentityField(f.field) ? '' : null" :type="inputType(f.field)">
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { blockSensitiveEgress } from '@/utils/demoEgress'
import demoProtectedFields from '@/mixins/demoProtectedFields'

const CLAIMS_METRIC_ID = 922;

// Catalog fields (CCLF / attribution column names) whose VALUES identify a
// patient, provider, practice or facility: identifiers, names, contact details,
// birth/death dates, record numbers and the provider/practice/facility columns.
// Descriptive suffixes (type, specialty, state, status, category, description,
// amounts) are categories and stay readable, as do claim dates, codes and all
// aggregates. ZIP stays readable, as in the patient search results grid.
const IDENTITY_TOKEN = /(^|_)(MBI|NPI|TIN|MRN|SSN|HICN|DOB|BIRTH|DEATH|PHONE|EMAIL|FAX|ADDRESS|ADDR|STREET|NAME|FIRST|LAST|PROVIDER|PRACTICE|FACILITY|ORGANIZATION|ORG|PCP|PHYSICIAN|CLINICIAN|ATTENDING|RENDERING|OPERATING|REFERRING|PATIENT|MEMBER)(_|$)/;
const IDENTITY_WHOLE = /^(CLM_ID|CLAIM_ID|CLM_NUM|CLAIM_NUMBER|CUR_CLM_UNIQ_ID|BENE_ID|OTHER_PATIENT_IDENTIFIER)$/;
const CATEGORY_SUFFIX = /_(TYPE|TYPE_CD|SPECIALTY|SPECIALTY_DESC|TAXONOMY|STATE|STATE_CD|STATUS|CATEGORY|CLASS|GROUP|FLAG|COUNT|CNT|DESC|DESCRIPTION|AGE|SEX|SEX_CD|GENDER|RACE|PAID|PAID_AMT|AMT|AMOUNT|UNITS|DAYS|LOS)$/;
const CATEGORY_WHOLE = /^(CLAIM_TYPE|CLM_TYPE|CLAIM_TYPE_CD|CARE_SETTING|CATEGORY)$/;

export function isExplorerIdentityField(name) {
  const n = String(name || '').toUpperCase();
  if (!n || CATEGORY_WHOLE.test(n) || CATEGORY_SUFFIX.test(n)) return false;
  return IDENTITY_WHOLE.test(n) || IDENTITY_TOKEN.test(n);
}

// Selectable Explorer datasets. Each is a studio metric the claims-pivot engine
// can introspect + pivot. metricId drives /pivot-fields/, /pivot/, /pivot-distinct/.
const DATASETS = [
  { metricId: 922, label: 'Claims (CCLF)' },
  { metricId: 924, label: 'Attribution Prospects' },
  { metricId: 925, label: 'Attribution Patient Report' },
];

export default {
  name: 'ExternalExplorer',
  components: { ExportMenu },
  mixins: [demoProtectedFields],
  // Typed terms over identity fields: the value-picker search (when the picked
  // field is an identity field), the result table's "contains" filter on
  // identity columns, and the creator's filter values on identity fields.
  // Demo Mode empties them and the tagged inputs are read-only. Filters on
  // category and numeric fields stay usable. Nothing here is persisted.
  demoProtectedFields() {
    const paths = [];
    if (this.pickerIsIdentity) paths.push('pickerSearch');
    for (const c of this.resultColumns) {
      if (c.sensitive && !c.num) paths.push(['colFilters', c.key, 'text']);
    }
    this.spec.filters.forEach((f, i) => {
      if (isExplorerIdentityField(f.field)) {
        paths.push(['spec', 'filters', String(i), 'valueText'], ['spec', 'filters', String(i), 'value']);
      }
    });
    return paths;
  },
  props: {
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      metricId: CLAIMS_METRIC_ID,
      datasets: DATASETS,
      reportDt: '',
      reportDates: [],
      clmFromDt: '2025-04-01',
      clmThruDt: '2026-03-31',
      careSetting: '',
      entityType: '',
      catalog: [],
      catalogLoading: false,
      spec: { dimensions: [], values: [], filters: [] },
      rows: [],
      loading: false,
      error: '',
      limit: 5000,
      capped: false,
      dragOver: null,
      drag: null,
      sortKey: null,
      sortDir: 'asc',
      colFilters: {},
      pickerKey: null,
      pickerField: null,
      pickerSearch: '',
      pickerOptions: [],
      pickerLoading: false,
      pickerError: '',
      aggOptions: ['sum', 'count_distinct', 'avg', 'min', 'max', 'count'],
      quickViews: [
        {
          id: 'op_category',
          label: 'OP by Category',
          spec: {
            dimensions: ['CATEGORY'],
            values: [
              { field: 'LINE_PAID', agg: 'sum' },
              { field: 'BENE_MBI_ID', agg: 'count_distinct' },
              { field: 'CLM_ID', agg: 'count_distinct' },
            ],
            filters: [{ field: 'CLAIM_TYPE', op: 'in', value: ['Outpatient'], valueText: 'Outpatient' }],
          },
        },
        {
          id: 'ptot_provider',
          label: 'PT/OT by Provider',
          spec: {
            dimensions: ['PROVIDER_NAME', 'PROVIDER_NPI'],
            values: [
              { field: 'UNITS', agg: 'sum' },
              { field: 'LINE_PAID', agg: 'sum' },
              { field: 'BENE_MBI_ID', agg: 'count_distinct' },
            ],
            filters: [{
              field: 'HCPCS_CODE', op: 'in',
              value: ['97110', '97112', '97113', '97116', '97140', '97150', '97530', '97535',
                '97161', '97162', '97163', '97165', '97166', '97167'],
              valueText: '97110, 97112, 97113, 97116, 97140, 97150, 97530, 97535, 97161, 97162, 97163, 97165, 97166, 97167',
            }],
          },
        },
        {
          id: 'drg_type',
          label: 'IP by DRG',
          spec: {
            dimensions: ['DRG_CODE', 'DRG_DESCRIPTION'],
            values: [
              { field: 'CLAIM_PAID_AMT', agg: 'sum' },
              { field: 'CLM_ID', agg: 'count_distinct' },
            ],
            filters: [{ field: 'CLAIM_TYPE', op: 'in', value: ['Inpatient'], valueText: 'Inpatient' }],
          },
        },
      ],
    };
  },
  computed: {
    panel() {
      return {
        metric_id: this.metricId,
        entity_id: this.entityId,
        contract_id: 'A5069',
        report_dt: this.reportDt,
        clm_from_dt: this.clmFromDt,
        clm_thru_dt: this.clmThruDt,
        clm_efctv_dt: this.clmThruDt,
        care_setting: this.careSetting || undefined,
        entity_type: this.entityType || undefined,
      };
    },
    resultColumns() {
      const cols = [];
      for (const d of this.spec.dimensions) {
        cols.push({ key: d.toLowerCase(), label: this.pretty(d), num: false, fmt: this.fmtText, sensitive: isExplorerIdentityField(d) });
      }
      for (const v of this.spec.values) {
        const alias = this.aliasFor(v);
        const isCurrency = /LINE_PAID|CLAIM_PAID_AMT|COST|AMT/i.test(v.field) && ['sum', 'avg', 'min', 'max'].includes(v.agg);
        const isCount = v.agg === 'count' || v.agg === 'count_distinct';
        const isDate = this.fieldType(v.field) === 'date';
        let fmt = this.fmtNumber;
        if (isCurrency) fmt = this.fmtCurrency;
        else if (isCount) fmt = this.fmtInt;
        else if (isDate) fmt = this.fmtText;
        cols.push({
          key: alias.toLowerCase(),
          label: `${this.aggLabel(v.agg)} ${this.pretty(v.field)}`,
          num: !isDate,
          summable: ['sum', 'count', 'count_distinct'].includes(v.agg) && !isDate,
          // Counts of an identity field are aggregates; min/max/avg/sum of one
          // can surface an individual value.
          sensitive: isExplorerIdentityField(v.field) && !isCount,
          fmt,
        });
      }
      return cols;
    },
    displayRows() {
      let out = this.rows;
      // typed column filters
      const active = this.resultColumns.filter(c => {
        const f = this.colFilters[c.key];
        if (!f) return false;
        if (c.num) {
          if (f.op === 'between') {
            return (f.min !== '' && f.min != null) || (f.max !== '' && f.max != null);
          }
          return !!f.op && f.value !== '' && f.value != null;
        }
        return (f.text != null && f.text !== '') || (f.selected && f.selected.length);
      });
      if (active.length) {
        out = out.filter(row => active.every((c) => {
          const f = this.colFilters[c.key];
          const raw = row[c.key];
          if (c.num) {
            const n = Number(raw);
            if (Number.isNaN(n)) return false;
            if (f.op === 'between') {
              const lo = f.min === '' || f.min == null ? -Infinity : Number(f.min);
              const hi = f.max === '' || f.max == null ? Infinity : Number(f.max);
              return n >= lo && n <= hi;
            }
            const val = Number(f.value);
            if (f.op === 'gte') return n >= val;
            if (f.op === 'lte') return n <= val;
            if (f.op === 'eq') return n === val;
            return true;
          }
          const sval = String(raw == null ? '' : raw);
          // discrete: must be in the selected set (when any selected)
          if (f.selected && f.selected.length && !f.selected.includes(sval)) return false;
          // contains: substring match (when text provided)
          if (f.text != null && f.text !== '' && !sval.toLowerCase().includes(String(f.text).toLowerCase())) return false;
          return true;
        }));
      }
      // sort
      if (this.sortKey) {
        const col = this.resultColumns.find(c => c.key === this.sortKey);
        const isNum = col && col.num;
        const dir = this.sortDir === 'asc' ? 1 : -1;
        out = out.slice().sort((a, b) => {
          let av = a[this.sortKey];
          let bv = b[this.sortKey];
          if (isNum) {
            av = Number(av); bv = Number(bv);
            if (Number.isNaN(av)) av = -Infinity;
            if (Number.isNaN(bv)) bv = -Infinity;
            return (av - bv) * dir;
          }
          av = String(av == null ? '' : av).toLowerCase();
          bv = String(bv == null ? '' : bv).toLowerCase();
          if (av < bv) return -1 * dir;
          if (av > bv) return 1 * dir;
          return 0;
        });
      }
      return out;
    },
    pickerIsIdentity() {
      return !!this.pickerKey && isExplorerIdentityField(this.pickerField);
    },
    filteredPickerOptions() {
      const q = this.pickerSearch.trim().toLowerCase();
      if (!q) return this.pickerOptions;
      return this.pickerOptions.filter(o => String(o).toLowerCase().includes(q));
    },
  },
  watch: {
    entityId() {
      this.fetchCatalog();
    },
    resultColumns: {
      immediate: true,
      handler() {
        this.syncColFilters();
      },
    },
  },
  mounted() {
    this.fetchReportDates();
    this._onDocClick = () => { if (this.pickerKey) this.pickerKey = null; };
    document.addEventListener('click', this._onDocClick);
  },
  beforeUnmount() {
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
  },
  methods: {
    isIdentityField: isExplorerIdentityField,
    pretty(name) {
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    fieldType(name) {
      const f = this.catalog.find(c => c.name === name);
      return f ? f.type : 'text';
    },
    isAggregatable(name) {
      const f = this.catalog.find(c => c.name === name);
      return f ? f.aggregatable : false;
    },
    aliasFor(v) {
      return v.alias || `${v.agg}_${v.field}`;
    },
    aggLabel(a) {
      return { sum: 'Sum', count: 'Count', count_distinct: 'Distinct', avg: 'Avg', min: 'Min', max: 'Max' }[a] || a;
    },
    opLabel(op) {
      return {
        eq: '=', in: 'in', between: 'between', gt: '>', gte: '≥', lt: '<', lte: '≤',
        contains: 'contains', is_null: 'is empty', not_null: 'is set',
      }[op] || op;
    },
    opsFor(field) {
      const t = this.fieldType(field);
      if (t === 'number') return ['eq', 'gt', 'gte', 'lt', 'lte', 'between', 'not_null', 'is_null'];
      if (t === 'date') return ['between', 'gte', 'lte', 'not_null', 'is_null'];
      return ['eq', 'in', 'contains', 'not_null', 'is_null'];
    },
    inputType(field) {
      const t = this.fieldType(field);
      if (t === 'date') return 'date';
      if (t === 'number') return 'number';
      return 'text';
    },
    // --- drag and drop ---
    onDragStart(field, source, evt, index) {
      this.drag = { field, source, index };
      if (evt && evt.dataTransfer) {
        evt.dataTransfer.effectAllowed = 'move';
        evt.dataTransfer.setData('text/plain', field);
      }
    },
    onDrop(target) {
      this.dragOver = null;
      const d = this.drag;
      this.drag = null;
      if (!d) return;
      // Remove from source shelf if it came from a shelf (move semantics)
      if (d.source === 'rows' && typeof d.index === 'number') this.spec.dimensions.splice(d.index, 1);
      else if (d.source === 'values' && typeof d.index === 'number') this.spec.values.splice(d.index, 1);
      else if (d.source === 'filters' && typeof d.index === 'number') this.spec.filters.splice(d.index, 1);
      this.addTo(target, d.field);
    },
    addTo(target, field) {
      if (target === 'rows') {
        if (!this.spec.dimensions.includes(field)) this.spec.dimensions.push(field);
      } else if (target === 'values') {
        const agg = this.fieldType(field) === 'date' ? 'max' : (this.isAggregatable(field) ? 'sum' : 'count_distinct');
        this.spec.values.push({ field, agg });
      } else if (target === 'filters') {
        // Text fields default to 'in' (multiselect value picker) — the natural
        // behavior for categorical columns. Number/date keep their first op.
        const t = this.fieldType(field);
        const op = t === 'text' ? 'in' : this.opsFor(field)[0];
        const f = { field, op };
        if (op === 'between') f.value = ['', ''];
        else if (op === 'in') { f.value = []; f.valueText = ''; }
        else f.value = '';
        this.spec.filters.push(f);
      }
    },
    onFilterOpChange(f) {
      if (f.op === 'between' && !Array.isArray(f.value)) f.value = ['', ''];
      else if (f.op === 'in') { if (!Array.isArray(f.value)) f.value = []; if (f.valueText === undefined) f.valueText = ''; }
      else if (['is_null', 'not_null'].includes(f.op)) { /* no value */ }
      else if (Array.isArray(f.value)) f.value = '';
    },
    onInChange(f) {
      f.value = (f.valueText || '').split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    },
    applyQuickView(qv) {
      this.spec = JSON.parse(JSON.stringify(qv.spec));
    },
    onScopeChange() {
      this.fetchCatalog();
    },
    onDatasetChange() {
      // Switching datasets invalidates the current spec, sort, filters, and
      // any cached results — the catalog is different per dataset.
      this.spec = { dimensions: [], values: [], filters: [] };
      this.rows = [];
      this.error = '';
      this.sortKey = null;
      this.sortDir = 'asc';
      this.pickerKey = null;
      this.fetchCatalog();
    },
    async fetchReportDates() {
      try {
        const { data } = await this.$axios.post('/api/claim-explorer/report-dates/', { contract_id: 'A5069' });
        this.reportDates = data;
        if (data.length && !this.reportDt) this.reportDt = data[0];
        this.fetchCatalog();
      } catch (e) {
        console.error('Failed to load report dates', e);
      }
    },
    async fetchCatalog() {
      if (!this.entityId || !this.reportDt) return;
      this.catalogLoading = true;
      try {
        const { data } = await this.$axios.post('/api/claim-explorer/pivot-fields/', this.panel);
        this.catalog = data;
      } catch (e) {
        console.error('Catalog fetch error', e);
        this.error = 'Failed to load fields.';
      } finally {
        this.catalogLoading = false;
      }
    },
    buildValues() {
      return this.spec.values.map(v => ({ field: v.field, agg: v.agg, alias: this.aliasFor(v) }));
    },
    buildFilters() {
      return this.spec.filters
        .filter(f => {
          if (['is_null', 'not_null'].includes(f.op)) return true;
          if (f.op === 'between') return Array.isArray(f.value) && f.value[0] !== '' && f.value[1] !== '';
          if (f.op === 'in') return Array.isArray(f.value) && f.value.length;
          return f.value !== '' && f.value != null;
        })
        .map(f => {
          const out = { field: f.field, op: f.op };
          if (!['is_null', 'not_null'].includes(f.op)) out.value = f.value;
          return out;
        });
    },
    async runNow() {
      if (!this.entityId || !this.reportDt) return;
      if (!this.spec.dimensions.length && !this.spec.values.length) { this.rows = []; return; }
      const payload = {
        ...this.panel,
        dimensions: this.spec.dimensions,
        values: this.buildValues(),
        filters: this.buildFilters(),
        limit: this.limit,
      };
      this.loading = true;
      this.error = '';
      try {
        const { data } = await this.$axios.post('/api/claim-explorer/pivot/', payload);
        this.rows = data;
        this.capped = data.length === this.limit;
      } catch (e) {
        console.error('Pivot run error', e);
        this.error = (e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || 'Pivot failed.';
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    syncColFilters() {
      // Rebuild colFilters keyed to current columns, preserving existing entries.
      const next = {};
      for (const c of this.resultColumns) {
        if (this.colFilters[c.key]) {
          next[c.key] = this.colFilters[c.key];
        } else if (c.num) {
          next[c.key] = { op: '', value: '', min: '', max: '' };
        } else {
          next[c.key] = { text: '', selected: [] };
        }
      }
      this.colFilters = next;
      // Drop a stale sort key if its column is gone.
      if (this.sortKey && !this.resultColumns.some(c => c.key === this.sortKey)) {
        this.sortKey = null;
      }
    },
    toggleSort(key) {
      if (this.sortKey !== key) {
        this.sortKey = key;
        this.sortDir = 'asc';
      } else if (this.sortDir === 'asc') {
        this.sortDir = 'desc';
      } else {
        this.sortKey = null;
        this.sortDir = 'asc';
      }
    },
    // --- discrete value picker (shared: result-table columns + creator 'in' filters) ---
    async _loadPicker(field, key) {
      // Toggle closed if already open on the same target.
      if (this.pickerKey === key) { this.pickerKey = null; this._pickerSel = null; return false; }
      this.pickerKey = key;
      this.pickerField = field;
      this.pickerSearch = '';
      this.pickerOptions = [];
      this.pickerError = '';
      this.pickerLoading = true;
      try {
        const { data } = await this.$axios.post('/api/claim-explorer/pivot-distinct/', {
          ...this.panel,
          field: String(field).toUpperCase(),
        });
        this.pickerOptions = (data.values || []).map(v => String(v));
      } catch (e) {
        console.error('Distinct values fetch error', e);
        this.pickerError = 'Failed to load values.';
      } finally {
        this.pickerLoading = false;
      }
      return true;
    },
    openPicker(col) {
      // Result-table column: selection lives on colFilters[key].selected.
      this._pickerSel = {
        get: () => this.colFilters[col.key].selected || [],
        set: (arr) => { this.colFilters[col.key].selected = arr; },
      };
      this._loadPicker(col.key, col.key);
    },
    openFilterPicker(f, i) {
      // Creator 'in' filter chip: selection lives on f.value, mirrored into f.valueText.
      this._pickerSel = {
        get: () => (Array.isArray(f.value) ? f.value : []),
        set: (arr) => {
          f.value = arr;
          f.valueText = arr.join(', ');
        },
      };
      this._loadPicker(f.field, 'filter-' + i);
    },
    pickerCurrent() {
      return this._pickerSel ? this._pickerSel.get() : [];
    },
    togglePick(opt) {
      if (!this._pickerSel) return;
      const cur = this._pickerSel.get().slice();
      const i = cur.indexOf(opt);
      if (i === -1) cur.push(opt);
      else cur.splice(i, 1);
      this._pickerSel.set(cur);
    },
    pickAll() {
      if (!this._pickerSel) return;
      const set = new Set(this._pickerSel.get());
      for (const o of this.filteredPickerOptions) set.add(o);
      this._pickerSel.set(Array.from(set));
    },
    pickNone() {
      if (this._pickerSel) this._pickerSel.set([]);
    },
    columnTotal(key) {
      return this.displayRows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    },
    exportCsv() {
      if (!this.displayRows.length) return;
      if (blockSensitiveEgress('export')) return;
      const cols = this.resultColumns;
      const header = cols.map(c => `"${c.label.replace(/"/g, '""')}"`).join(',');
      const lines = this.displayRows.map(r => cols.map(c => {
        const v = r[c.key];
        if (v == null) return '';
        if (c.num) return Math.round(Number(v) * 100) / 100;
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(','));
      const csv = [header, ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'claims_pivot.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    fmtCurrency(v) {
      if (v == null || v === '') return '—';
      return '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
    },
    fmtInt(v) {
      if (v == null || v === '') return '—';
      return Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
    },
    fmtNumber(v) {
      if (v == null || v === '') return '—';
      return Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 });
    },
    fmtText(v) {
      return v == null || v === '' ? '—' : v;
    },
  },
};
</script>

<style scoped>
.filter-label {
  font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: #7D7770; margin-bottom: 4px;
}
.filter-input {
  padding: 5px 8px; border: 1px solid #E5E1DB; border-radius: 5px;
  font-size: 12px; color: #374151; background: #fff; font-family: inherit;
}
.filter-input:focus { outline: none; border-color: #A9A29A; }

/* Two-column: big results left, stacked field panel right */
.explorer-grid {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 16px;
  align-items: start;
}
.results-pane { min-width: 0; }
.field-pane { display: flex; flex-direction: column; gap: 12px; }

.drop-box {
  background: #fff; border: 1px solid #E5E1DB; border-radius: 6px;
  padding: 10px 12px; min-height: 64px;
}
.available-box { background: #FBFAF8; }
.drop-box.drop-over { border-color: #A9A29A; background: #F5F3EF; }
.drop-title {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #221F1C; margin-bottom: 8px;
}
.drop-empty { font-size: 11px; color: #C4BEB6; font-style: italic; }

.chip-bin { display: flex; flex-wrap: wrap; gap: 5px; }
.chip {
  display: inline-block; padding: 3px 8px; border-radius: 4px;
  font-size: 11px; cursor: grab; user-select: none;
  background: #EEF2FF; color: #3730A3;
}
.chip:hover { background: #E0E7FF; }
.chip:active { cursor: grabbing; }

.placed-chip {
  display: flex; align-items: center; gap: 6px;
  background: #F5F3EF; border: 1px solid #E5E1DB; border-radius: 5px;
  padding: 5px 8px; margin-bottom: 6px; font-size: 12px; cursor: grab;
}
.placed-chip-tall { display: block; }
.placed-label { color: #374151; font-weight: 500; }
.placed-x { color: #A9A29A; cursor: pointer; font-size: 14px; line-height: 1; padding: 0 2px; }
.placed-x:hover { color: #B91C1C; }
.mini-select {
  border: 1px solid #E5E1DB; border-radius: 4px; font-size: 11px;
  padding: 2px 4px; background: #fff; color: #374151; font-family: inherit;
}
.mini-input {
  border: 1px solid #E5E1DB; border-radius: 4px; font-size: 11px;
  padding: 3px 6px; width: 90px; color: #374151; font-family: inherit; box-sizing: border-box;
}
.mini-input:focus, .mini-select:focus { outline: none; border-color: #A9A29A; }

.qv-btn {
  padding: 5px 12px; background: #221F1C; color: #fff; border: none;
  border-radius: 5px; font-size: 11px; font-weight: 500; cursor: pointer; font-family: inherit;
}
.qv-btn:hover { background: #3a3632; }

.run-btn {
  width: 100%; padding: 8px 22px; background: #2563EB; color: #fff; border: none;
  border-radius: 5px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
}
.run-btn:hover { background: #1D4ED8; }
.run-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.total-row { font-weight: 600; border-top: 2px solid #E5E1DB; }

/* sortable headers */
.ext-table th.sortable { cursor: pointer; user-select: none; white-space: nowrap; }
.ext-table th.sortable:hover { background: #F3F1ED; }
.ext-table th .th-label { vertical-align: middle; }
.ext-table th .sort-ind {
  display: inline-block; width: 10px; margin-left: 3px;
  font-size: 9px; color: #2563EB; vertical-align: middle;
}

/* per-column filter row */
.ext-table thead tr.filter-row th {
  padding: 3px 6px 5px; background: #FAF9F7; border-top: 1px solid #EFEDE9;
  cursor: default; font-weight: 400;
}
.ext-table thead tr.filter-row th:hover { background: #FAF9F7; }
.col-filter-input {
  width: 100%; border: 1px solid #E5E1DB; border-radius: 4px;
  font-size: 11px; padding: 2px 5px; color: #374151; background: #fff;
  font-family: inherit; box-sizing: border-box;
}
.col-filter-input:focus { outline: none; border-color: #A9A29A; }
.col-filter-num { display: flex; gap: 3px; align-items: center; }
.col-filter-op {
  border: 1px solid #E5E1DB; border-radius: 4px; font-size: 11px;
  padding: 2px 2px; background: #fff; color: #374151; font-family: inherit; flex: 0 0 auto;
}
.col-filter-op:focus { outline: none; border-color: #A9A29A; }
.col-filter-num .col-filter-input { min-width: 0; }

/* creator 'in' filter row: comma input + discrete picker trigger */
.filter-in-row { display: flex; gap: 3px; align-items: center; width: 100%; position: relative; }
.filter-in-row .value-picker { right: 0; left: auto; min-width: 180px; }

/* text filter: contains + discrete picker trigger */
.col-filter-text { display: flex; gap: 3px; align-items: center; position: relative; }
.col-filter-text .col-filter-input { min-width: 0; }
.col-filter-pick {
  flex: 0 0 auto; border: 1px solid #E5E1DB; border-radius: 4px;
  background: #fff; color: #7D7770; font-size: 10px; line-height: 1;
  padding: 3px 5px; cursor: pointer; font-family: inherit;
}
.col-filter-pick:hover { background: #F3F1ED; color: #374151; }
.col-filter-pick.active { background: #2563EB; color: #fff; border-color: #2563EB; }

/* discrete value picker dropdown */
.value-picker {
  position: absolute; z-index: 30; margin-top: 2px; min-width: 200px; max-width: 320px;
  background: #fff; border: 1px solid #D9D4CD; border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.14); padding: 6px;
}
.vp-head { margin-bottom: 6px; }
.vp-head .col-filter-input { width: 100%; }
.vp-msg { font-size: 11px; color: #A9A29A; padding: 8px 2px; }
.vp-err { color: #B91C1C; }
.vp-actions { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.vp-link {
  background: none; border: none; color: #2563EB; font-size: 11px;
  cursor: pointer; padding: 0; font-family: inherit;
}
.vp-link:hover { text-decoration: underline; }
.vp-list { max-height: 220px; overflow-y: auto; }
.vp-item {
  display: flex; align-items: center; gap: 6px; padding: 3px 2px;
  font-size: 12px; color: #374151; cursor: pointer; font-weight: 400;
}
.vp-item:hover { background: #F3F1ED; }
.vp-val { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vp-foot { margin-top: 6px; display: flex; justify-content: flex-end; }
.vp-apply {
  background: #221F1C; color: #fff; border: none; border-radius: 4px;
  font-size: 11px; padding: 4px 12px; cursor: pointer; font-family: inherit;
}
.vp-apply:hover { background: #3a3632; }
</style>
